import { useCallback, useEffect, useState } from 'react';
import axios from 'utils/axios';
import useAuth from './useAuth';

export type Community = {
  documentId?: string;
  image?: string | null;
  label?: string;
  name: string;
  bounds?: string | null;
  onboardingComplete?: boolean | null;
  members?: Array<{
    documentId: string;
    firstName: string;
    lastName: string;
    email: string;
  }>;
};

type UseCommunitiesArgs = {
  limit?: number;
  start?: number;
};

const MANAGEMENT_ROLES = new Set(['Super Admin', 'Admin', 'Manager']);

// Query to get all communities a user has a membership in, via communityRoleMemberships.
const MEMBERSHIPS_COMMUNITIES_QUERY = `
  query GetMyCommunities($userDocumentId: ID!) {
    communityRoleMemberships(filters: { user: { documentId: { eq: $userDocumentId } } }) {
      communityRole { name }
      community {
        documentId
        name
        bounds
        onboarding {
          onboardingComplete
        }
        members {
          documentId
          firstName
          lastName
          email
        }
      }
    }
  }
`;

// Query used for HOA users who can see all communities.
const ALL_COMMUNITIES_QUERY = `
  query Communities($pagination: PaginationArg) {
    communities_connection(pagination: $pagination) {
      nodes {
        documentId
        name
        bounds
        onboarding {
          onboardingComplete
        }
        members {
          documentId
          firstName
          lastName
          email
        }
      }
    }
  }
`;

export default function useCommunities({ limit = 100, start = 0 }: UseCommunitiesArgs = {}) {
  const [communities, setCommunities] = useState<Community[]>([]);
  const { user } = useAuth();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<unknown>(null);
  const [hasManagementRole, setHasManagementRole] = useState(false);

  const role = typeof user?.role === 'string' ? user.role : (user?.role as any)?.name;
  const isHOAAdmin = role === 'HOA';

  useEffect(() => {
    if (!user?.documentId) return;
    let ignore = false;
    setLoading(true);
    setError(null);

    const request = isHOAAdmin
      ? axios.post('/graphql', {
          query: ALL_COMMUNITIES_QUERY,
          variables: { pagination: { limit, start } },
        }).then((res) => ({ nodes: res.data?.data?.communities_connection?.nodes ?? [], hasManagement: true }))
      : axios.post('/graphql', {
          query: MEMBERSHIPS_COMMUNITIES_QUERY,
          variables: { userDocumentId: user.documentId },
        }).then((res) => {
          // Deduplicate: user may have multiple roles in the same community
          const memberships: any[] = res.data?.data?.communityRoleMemberships ?? [];
          const seen = new Set<string>();
          const nodes: any[] = [];
          let hasManagement = false;
          for (const m of memberships) {
            if (MANAGEMENT_ROLES.has(m.communityRole?.name)) hasManagement = true;
            if (m.community?.documentId && !seen.has(m.community.documentId)) {
              seen.add(m.community.documentId);
              nodes.push(m.community);
            }
          }
          return { nodes, hasManagement };
        });

    request
      .then(({ nodes, hasManagement }) => {
        if (ignore) return;
        setHasManagementRole(hasManagement);
        const items: Community[] = nodes.map((n: any) => ({
          name: n.name,
          documentId: n.documentId,
          bounds: n.bounds,
          onboardingComplete: n.onboarding?.onboardingComplete ?? false,
          members: n.members ?? [],
        }));
        setCommunities(items);
      })
      .catch((e) => setError(e))
      .finally(() => {
        if (!ignore) setLoading(false);
      });

    return () => { ignore = true; };
  }, [limit, start, isHOAAdmin, user?.documentId]);

  // GraphQL mutation to update current user's active community
  const updateActiveCommunity = useCallback(async (userId: string | number, community: string) => {
    // GraphQL: treat app_active_community as a string field; empty string clears it
    const mutation = `
     mutation Mutation($updateUsersPermissionsUserId: ID!, $data: UsersPermissionsUserInput!) {
        updateUsersPermissionsUser(id: $updateUsersPermissionsUserId, data: $data) {
            data {
            app_active_community
            }
        }
        }
    `;
    await axios.post('/graphql', {
      query: mutation,
      variables: { updateUsersPermissionsUserId: String(userId), data: { app_active_community: community } }
    });
  }, []);

  return { communities, loading, error, hasManagementRole, updateActiveCommunity };
}
